/**
 * Утилиты для форматирования данных
 *
 * Основные функции:
 * - Форматирование ФИО (полное и инициалы)
 * - Очистка и форматирование телефонных номеров
 * - Преобразование дат между различными форматами
 * - Конвертация дат для API
 *
 * Особенности:
 * - Защита от null/undefined значений
 * - Поддержка русского формата дат
 * - Гибкая настройка форматов вывода
 */

export interface IPersonName {
  last_name?: string | null
  first_name?: string | null
  middle_name?: string | null
  full_name?: string | null
}

/**
 * Форматирует ФИО в одну строку
 * @param {object} data - Объект с полями имени
 * @param {string} [data.last_name] - Фамилия
 * @param {string} [data.first_name] - Имя
 * @param {string} [data.middle_name] - Отчество
 * @returns {string} Полное имя в формате "Фамилия Имя Отчество"
 *
 * Пример:
 * getFullName({last_name: "Иванов", first_name: "Иван", middle_name: "Иванович"})
 * -> "Иванов Иван Иванович"
 */
export const getFullName = (data: {
  last_name?: string | null | undefined
  first_name?: string | null | undefined
  middle_name?: string | null | undefined
}): string => `${data?.last_name ?? ''} ${data?.first_name ?? ''} ${data?.middle_name ?? ''}`.trim()

/** Полное ФИО как с бэка: `full_name` или сборка из частей / строка как есть */
export const getPersonDisplayName = (person?: IPersonName | string | null): string => {
  if (!person)
    return ''
  if (typeof person === 'string')
    return person.trim()
  if (person.full_name?.trim())
    return person.full_name.trim()

  return getFullName(person)
}

function initialsFromWords(value?: string | null): string {
  if (!value?.trim())
    return ''

  return value
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
    .map(word => `${word[0].toLocaleUpperCase('ru-RU')}.`)
    .join('')
}

function formatShortNameFromString(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (!parts.length)
    return ''

  const [lastName, ...rest] = parts
  const initials = rest.map(part => initialsFromWords(part)).join('')

  return initials ? `${lastName} ${initials}` : lastName
}

/**
 * Форматирует ФИО в инициалы
 * @param {object} data - Объект с полями имени
 * @param {string} [data.last_name] - Фамилия
 * @param {string} [data.first_name] - Имя
 * @param {string} [data.middle_name] - Отчество
 * @param {boolean} [lastNameInitial] - Сокращать ли фамилию до первой буквы
 * @returns {string} Инициалы в формате "Иванов И.И." или "И И.И." (при lastNameInitial=true)
 *
 * Примеры:
 * getInitialsFullName({last_name: "Иванов", first_name: "Иван", middle_name: "Иванович"})
 * -> "Иванов И.И."
 *
 * getInitialsFullName({last_name: "Иванов", first_name: "Иван"}, true)
 * -> "И И."
 */
export const getInitialsFullName = (
  data: IPersonName,
  lastNameInitial = false,
): string => {
  if (!lastNameInitial)
    return formatShortName(data)

  const lastInitial = data.last_name?.trim()?.[0]
  if (!lastInitial)
    return ''

  const initials = `${initialsFromWords(data.first_name)}${initialsFromWords(data.middle_name)}`

  return initials ? `${lastInitial} ${initials}` : lastInitial
}

/**
 * Очищает номер телефона от всех нецифровых символов
 * @param {string} phoneNumber - Номер телефона в произвольном формате
 * @returns {string} Номер, содержащий только цифры
 *
 * Пример:
 * formatAndClearPhoneNumber("+7 (123) 456-78-90") -> "71234567890"
 */
export const formatAndClearPhoneNumber = (phoneNumber: string): string =>
  phoneNumber?.replace(/\D/g, '')

/**
 * Преобразует дату рождения из формата ДДММГГГГ в ISO (YYYY-MM-DD)
 * @param {string} birthday - Дата в формате "ддммгггг" (например "17081990")
 * @returns {string} Дата в формате "YYYY-MM-DD" ("1990-08-17")
 *
 * Пример:
 * formatBirthday("17081990") -> "1990-08-17"
 */
export const formatBirthday = (birthday: string): string => {
  const date = String(birthday).replace(/\D+/g, '')
  const day = date.slice(0, 2)
  const month = date.slice(2, 4)
  const year = date.slice(4, 8)
  return `${year}-${month}-${day}`
}

/**
 * Форматирует номер телефона в российский стандартный вид
 * @param {string} phone - Номер телефона (11 цифр, начинается с 7 или 8)
 * @returns {string} Номер в формате "+7 (XXX) XXX-XX-XX" или исходная строка если невалидный ввод
 *
 * Пример:
 * formatPhoneNumber("79161234567") -> "+7 (916) 123-45-67"
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length !== 11) return phone
  return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9, 11)}`
}

/**
 * Форматирует дату-время в русский локализованный формат
 * @param {string|null|undefined} dateTime - Дата в ISO формате или null/undefined
 * @returns {string} Дата в формате "дд.мм.гггг, чч:мм" или пустая строка для невалидного ввода
 *
 * Пример:
 * formatDateTime("2025-06-14T11:25:26Z") -> "14.06.2025, 14:25"
 */
export const formatDateTime = (dateTime: string | Date | null | undefined): string => {
  if (!dateTime) return ''
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime)
  if (isNaN(date.getTime())) return ''

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day}.${month}.${year}, ${hours}:${minutes}`
}

/**
 * Конвертирует дату в формат API (YYYY-MM-DD)
 * @param {Date|string|null} [date] - Дата для конвертации (может быть Date, строкой ISO или null/undefined)
 * @returns {string|null} Дата в формате "YYYY-MM-DD" или null для невалидного ввода
 *
 * Примеры:
 * toApiDate(new Date(2025, 5, 14)) -> "2025-06-14"
 * toApiDate("2025-06-14T11:25:26Z") -> "2025-06-14"
 * toApiDate(null) -> null
 */
export function toApiDate(date?: Date | string | null): string | null {
  if (!date) return null
  const d = new Date(date)
  if (isNaN(d.getTime())) return null

  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Форматирует дату мероприятия в кратком формате
 * @param {string|object|null|undefined} dateTime - Дата в ISO формате или объект с _custom.value
 * @returns {string} Дата в формате "14 авг, 10:00" или пустая строка
 *
 * Примеры:
 * formatResponsibilityDate("2025-08-14T10:00:00Z") -> "14 авг, 10:00"
 * formatResponsibilityDate({_custom: {value: "2025-08-14T10:00:00Z"}}) -> "14 авг, 10:00"
 */
export const formatResponsibilityDate = (dateTime: string | { _custom?: { value: string } } | null | undefined): string => {
  // Извлекаем строку даты
  let dateString: string | null = null

  if (typeof dateTime === 'object' && dateTime && '_custom' in dateTime && dateTime._custom?.value) {
    dateString = dateTime._custom.value
  } else if (typeof dateTime === 'string') {
    dateString = dateTime
  }

  if (!dateString) return ''

  // Создаем Date объект с явным приведением типов
  const date = new Date(dateString as string)
  if (isNaN(date.getTime())) return ''

  const day = date.getDate()
  const month = date.toLocaleString('ru-RU', { month: 'short' }).replace('.', '')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day} ${month}, ${hours}:${minutes}`
}

/**
 * Форматирует ФИО в сокращенный вид "Фамилия И.О."
 * Фамилия целиком; имя и отчество — первая буква каждого слова (и части через дефис).
 *
 * Примеры:
 * formatShortName({ last_name: "Иванов", first_name: "Иван", middle_name: "Иванович" })
 * -> "Иванов И.И."
 * formatShortName({ last_name: "Буганков", first_name: "Жан Поль", middle_name: "Сергеевич" })
 * -> "Буганков Ж.П.С."
 * formatShortName("Буганков Алексей Сергеевич")
 * -> "Буганков А.С."
 */
export const formatShortName = (person?: IPersonName | string | null): string => {
  if (!person)
    return ''

  if (typeof person === 'string')
    return formatShortNameFromString(person)

  const lastName = person.last_name?.trim()
  if (lastName) {
    const initials = `${initialsFromWords(person.first_name)}${initialsFromWords(person.middle_name)}`

    return initials ? `${lastName} ${initials}` : lastName
  }

  if (person.full_name?.trim())
    return formatShortNameFromString(person.full_name)

  return ''
}

/**
 * Форматирует дату в формате "дд.мм.гггг" (без времени)
 * @param {string|Date|null} date - Дата в ISO или Date
 * @returns {string} "15.03.1967" или пустая строка
 */
export const formatDateOnly = (date: string | Date | null | undefined): string => {
  if (!date) return ''
  if (typeof date === 'string' && /^\d{2}\.\d{2}$/.test(date.trim())) {
    return date.trim()
  }
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('ru-RU')
}

/**
 * Форматирует время в формате "чч:мм" (без даты)
 * @param {string|Date|null} date - Дата/время в ISO или Date
 * @returns {string} "14:35" или пустая строка
 */
export const formatTimeOnly = (date: string | Date | null | undefined): string => {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Разбирает строку ФИО на фамилию, имя и отчество.
 * Отсутствующие части заменяются пустой строкой.
 */
export const parseFullName = (fullName: string) => {
  const parts = fullName.trim().split(' ')

  return {
    last_name: parts[0] || '',
    first_name: parts[1] || '',
    middle_name: parts[2] || '',
  }
}

const RELATIVE_TIME_MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

/** Полные названия месяцев в родительном падеже: «14 июня 2024» */
const MONTHS_LONG_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
]

/**
 * Дата длинным русским форматом: «14 июня 2024».
 *
 * @param iso  ISO-строка или Date
 * @returns    Локализованная дата или пустая строка при невалидном вводе
 */
export const formatLongDate = (iso: string | Date | null | undefined): string => {
  if (!iso) return ''
  const date = iso instanceof Date ? iso : new Date(iso)
  if (isNaN(date.getTime())) return ''

  return `${date.getDate()} ${MONTHS_LONG_GENITIVE[date.getMonth()]} ${date.getFullYear()}`
}

/**
 * Дата и время длинным русским форматом: «14 июня 2024, 17:38».
 * Композиция {@link formatLongDate} и {@link formatTimeOnly}.
 */
export const formatLongDateTime = (iso: string | Date | null | undefined): string => {
  const datePart = formatLongDate(iso)
  const timePart = formatTimeOnly(iso)
  if (!datePart) return ''
  if (!timePart) return datePart
  return `${datePart}, ${timePart}`
}

/**
 * Форматирует ISO-дату относительно текущего момента — для списков уведомлений и подобных
 * лент, где важно видеть «свежесть».
 *
 * Шкала:
 * - меньше 60 секунд        → «Только что»
 * - меньше 60 минут         → «N мин назад»
 * - меньше 24 часов         → «N ч назад»
 * - вчера (< 48 часов и вчерашняя календарная дата) → «Вчера»
 * - меньше 7 дней           → «N дн назад»
 * - дальше                  → «14 авг, 10:30»
 *
 * Параметр now нужен для реактивного пересчёта (см. {@link useReactiveNow}) — передавайте
 * туда значение из useReactiveNow(), и при тике интервала строка обновится сама.
 *
 * @param iso         ISO-строка с датой/временем (или Date)
 * @param now         «Текущий момент» в миллисекундах; по умолчанию `Date.now()`
 * @returns           Локализованная относительная строка или пустая строка при невалидном вводе
 */
export const formatRelativeTime = (iso: string | Date | null | undefined, now: number = Date.now()): string => {
  if (!iso) return ''
  const date = iso instanceof Date ? iso : new Date(iso)
  if (isNaN(date.getTime())) return ''

  const diffSec = Math.floor((now - date.getTime()) / 1000)

  if (diffSec < 60) return 'Только что'

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} мин назад`

  const diffHour = Math.floor(diffMin / 60)
  if (diffHour < 24) return `${diffHour} ч назад`

  // «Вчера» — если разница меньше 48 часов И дата соответствует вчерашнему календарному дню
  const nowDate = new Date(now)
  const yesterday = new Date(nowDate)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday = date.getFullYear() === yesterday.getFullYear()
    && date.getMonth() === yesterday.getMonth()
    && date.getDate() === yesterday.getDate()
  if (isYesterday) return 'Вчера'

  const diffDay = Math.floor(diffHour / 24)
  if (diffDay < 7) return `${diffDay} дн назад`

  const day = date.getDate()
  const month = RELATIVE_TIME_MONTHS_SHORT[date.getMonth()]
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day} ${month}, ${hours}:${minutes}`
}

/**
 * Дневная метка со временем для списков уведомлений:
 * - сегодня     → «Сегодня, 08:00»
 * - вчера       → «Вчера, 10:30»
 * - этот год    → «27 Мар, 10:30»
 * - другой год  → «27 Мар, 2025, 10:30»
 *
 * Композиция {@link formatRelativeDay} и {@link formatTimeOnly}.
 */
export const formatRelativeDayWithTime = (iso: string | Date | null | undefined, now: number = Date.now()): string => {
  const day = formatRelativeDay(iso, now)
  const time = formatTimeOnly(iso)
  if (!day) return time
  if (!time) return day
  return `${day}, ${time}`
}

/**
 * Короткая «дневная» метка для уведомлений:
 * - сегодня     → «Сегодня»
 * - вчера       → «Вчера»
 * - этот год    → «27 Мар»
 * - другой год  → «27 Мар, 2025»
 *
 * Отличается от {@link formatRelativeTime} тем, что всегда возвращает календарную дату,
 * не «N мин назад».
 */
export const formatRelativeDay = (iso: string | Date | null | undefined, now: number = Date.now()): string => {
  if (!iso) return ''
  const date = iso instanceof Date ? iso : new Date(iso)
  if (isNaN(date.getTime())) return ''

  const nowDate = new Date(now)
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

  if (isSameDay(date, nowDate)) return 'Сегодня'

  const yesterday = new Date(nowDate)
  yesterday.setDate(yesterday.getDate() - 1)
  if (isSameDay(date, yesterday)) return 'Вчера'

  const day = date.getDate()
  const month = RELATIVE_TIME_MONTHS_SHORT[date.getMonth()]
  const monthCap = month.charAt(0).toUpperCase() + month.slice(1)
  const sameYear = date.getFullYear() === nowDate.getFullYear()

  return sameYear ? `${day} ${monthCap}` : `${day} ${monthCap}, ${date.getFullYear()}`
}
